import StyledContainer from '@/components/styled/StyledContainer';
import StyledListHeader from '@/components/styled/StyledListHeader';
import StyledNextPreviousButtons from '@/components/styled/StyledNextPreviousButtons';
import StyledSelect from '@/components/styled/StyledSelect';
import StyledTable from '@/components/styled/StyledTable';
import { PAGE_SIZE } from '@/constants/pageSize';
import { useGetDistributorsEstimateStats } from '@/hooks/useStats';
import useTableControls from '@/hooks/useTableControls';
import { LabelValue } from '@/types/global';
import { DISTRIBUTORS_ESTIMATE_STATS_COLUMNS } from '@/types/stats/DistributorEstimateStatsColumnDef';
import { eachMonthOfInterval, format } from 'date-fns';
import { useMemo } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useTranslation } from '@/i18n';
import { useTranslatedConst } from '@/hooks/useTranslatedConst';
import { useLanguage } from '@/contexts/LanguageContext';
import { LOCALES_MAP } from '@/constants/localeMap';

type FiltersType = {
  agency?: LabelValue<string>;
  month?: LabelValue<string>;
};

const ReceiverStatsPage = () => {
  const { currentLanguage } = useLanguage();
  const { t } = useTranslation();
  const getTranslatedConst = useTranslatedConst();
  const { control } = useForm<FiltersType>({
    mode: 'onChange',
    defaultValues: {
      agency: undefined,
      month: undefined,
    },
  });

  const selectedAgency = useWatch({ control, name: 'agency' });
  const selectedMonth = useWatch({ control, name: 'month' });

  const { page, setPage, sorting, setSorting, sortField, sortOrder } =
    useTableControls();

  const filters = useMemo(
    () => ({
      agencyId: selectedAgency?.value ?? undefined,
      month: selectedMonth?.value ?? undefined,
    }),
    [selectedAgency?.value, selectedMonth?.value],
  );

  const { data: DistributorsEstimateStatsResponse } =
    useGetDistributorsEstimateStats({
      page,
      pageSize: PAGE_SIZE,
      sortField,
      sortOrder,
      filters,
    });

  const monthOptions = useMemo(
    () =>
      eachMonthOfInterval({ start: new Date(2025, 0, 1), end: new Date() }).map(
        (month) => ({
          label: format(month, 'LLLL yyyy', {
            locale: LOCALES_MAP[currentLanguage],
          }),
          value: format(month, 'yyyy-MM'),
        }),
      ),
    [currentLanguage],
  );

  const agenciesOptions = useMemo(
    () =>
      DistributorsEstimateStatsResponse?.agencies.map(({ id, name }) => ({
        label: name,
        value: id,
      })),
    [DistributorsEstimateStatsResponse?.agencies],
  );

  return (
    <StyledContainer>
      <StyledListHeader
        title={t('stats.title')}
        subTitle={t('stats.subTitle')}
      />
      <div className="flex flex-col w-full h-auto p-6 gap-6">
        <div className="flex flex-col gap-2">
          <span className="text-xs font-normal text-gray-600">
            {t('stats.filterBy')}
          </span>
          <div className="flex flex-col md:flex-row justify-between gap-4">
            <div className="flex flex-col md:flex-row gap-4">
              <StyledSelect
                className="min-w-1/3 md:min-w-48 lg:min-w-60 xl:min-w-80"
                control={control}
                options={agenciesOptions}
                name="agency"
                placeholder={t('stats.distributor')}
                isClearable={true}
              />
              <StyledSelect
                className="min-w-1/3 md:min-w-48 lg:min-w-60 xl:min-w-80"
                control={control}
                options={monthOptions}
                name="month"
                placeholder={t('stats.month')}
                isClearable={true}
              />
            </div>
          </div>
        </div>
        <StyledTable
          columns={getTranslatedConst(DISTRIBUTORS_ESTIMATE_STATS_COLUMNS)}
          data={DistributorsEstimateStatsResponse?.agencies ?? []}
          getRowId={(row) => row.id}
          sorting={sorting}
          setSorting={setSorting}
        />
        <StyledNextPreviousButtons
          dataLength={DistributorsEstimateStatsResponse?.total ?? 0}
          page={page}
          pageSize={PAGE_SIZE}
          setPage={setPage}
          label={t('stats.distributor')}
        />
      </div>
    </StyledContainer>
  );
};

export default ReceiverStatsPage;
